// ---------------------------------------------------------------------------
// Speech hooks.
//
// useSpeechRecorder combines two capture paths that run together:
//  - LIVE TRANSCRIPTION via the Web Speech API (SpeechRecognition). Available in
//    Chrome/Edge; NOT available in WebView2 (the desktop build) — there the user
//    types/edits the transcript and the UI says so up front.
//  - AUDIO RECORDING via MediaRecorder (honors the configured microphone).
//    Works everywhere, including the desktop build, and powers delivery
//    playback in the feedback panel.
//
// useTextToSpeech wraps speechSynthesis for "Read Aloud".
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { mergeRecordings } from '../services/audioMerge';

// Minimal typings for the non-standard SpeechRecognition API.
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>;
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** True when live speech-to-text is available (Chrome/Edge; not WebView2). */
export function isLiveTranscriptionSupported(): boolean {
  return typeof window !== 'undefined' && !!getRecognitionCtor();
}

/** True when microphone audio recording is available. */
export function isAudioRecordingSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const mime of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

/**
 * Video container by platform: Chromium (Windows/Linux WebView2) records webm;
 * WKWebView on macOS (the Mac desktop build) has MediaRecorder but only
 * supports mp4/H.264 — so mp4 is a required fallback, not an afterthought.
 */
function pickVideoMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const mime of ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return '';
}

export interface RecordedAudio {
  mime: string;
  bytes: Uint8Array;
}

export interface SpeechRecorder {
  /** Live transcription available (Web Speech API). */
  supported: boolean;
  /** Microphone audio capture available (MediaRecorder). */
  audioSupported: boolean;
  recording: boolean;
  transcript: string;
  interim: string;
  elapsedSeconds: number;
  /** Object URL for playback of the recorded answer (after stop), or null. */
  audioUrl: string | null;
  /** Object URL for playback of the recorded webcam video (after stop), or null. */
  videoUrl: string | null;
  /** Live camera stream while recording with video, for the preview element. */
  previewStream: MediaStream | null;
  /** Why the camera couldn't be used this recording (empty when fine). */
  videoError: string;
  start: () => void;
  stop: () => { transcript: string; durationSeconds: number };
  reset: () => void;
  setTranscript: (t: string) => void;
  /** Resolve the recorded audio bytes (waits for the encoder to finalize). */
  getAudio: () => Promise<RecordedAudio | null>;
  /** Resolve the recorded video bytes (waits for the encoder to finalize). */
  getVideo: () => Promise<RecordedAudio | null>;
}

export function useSpeechRecorder(options?: {
  microphoneId?: string;
  /** Also capture the webcam (video practice mode). */
  video?: boolean;
}): SpeechRecorder {
  const supported = isLiveTranscriptionSupported();
  const audioSupported = isAudioRecordingSupported();
  const microphoneId = options?.microphoneId ?? '';
  const wantVideo = !!options?.video;

  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [videoError, setVideoError] = useState('');

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Mirror `recording` in a ref so the long-lived `onend` handler reads the
  // CURRENT value, not the (false) value captured when start() ran. Without
  // this, the auto-restart below never fires and transcription silently stops
  // at the first pause (Chrome ends recognition on silence).
  const recordingRef = useRef(false);
  const finalRef = useRef('');
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Audio capture state. Each Record/Resume press runs a FRESH MediaRecorder,
  // so an answer is a list of independent takes (segments). Raw byte
  // concatenation of takes is not a valid audio file — decoders stop after the
  // first take — so multi-take answers are merged into one WAV on stop.
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]); // current take only
  const segmentsRef = useRef<Blob[]>([]); // finalized takes
  const finalAudioRef = useRef<Blob | null>(null); // whole-answer blob (merged)
  // Guards stale async finalizations (quick Stop→Resume→Stop, or reset).
  const finalizeRunRef = useRef(0);
  const mimeRef = useRef('');
  const recorderStoppedRef = useRef<Promise<void>>(Promise.resolve());
  const audioUrlRef = useRef<string | null>(null);

  // Webcam capture state (video practice mode). A SECOND MediaRecorder runs on
  // the full audio+video stream; the audio recorder above keeps recording a
  // pure-audio stream so Whisper transcription and audio playback are untouched.
  const videoRecorderRef = useRef<MediaRecorder | null>(null);
  const videoChunksRef = useRef<Blob[]>([]);
  const videoMimeRef = useRef('');
  const videoStoppedRef = useRef<Promise<void>>(Promise.resolve());
  const videoUrlRef = useRef<string | null>(null);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const revokeAudioUrl = () => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setAudioUrl(null);
  };

  const revokeVideoUrl = () => {
    if (videoUrlRef.current) {
      URL.revokeObjectURL(videoUrlRef.current);
      videoUrlRef.current = null;
    }
    setVideoUrl(null);
  };

  // Fold the just-finished take into segments and rebuild the whole-answer
  // blob (merged into one WAV when the answer spans several takes).
  const finalizeAudio = useCallback(async () => {
    if (chunksRef.current.length) {
      segmentsRef.current.push(new Blob(chunksRef.current, { type: mimeRef.current || undefined }));
      chunksRef.current = [];
    }
    const segments = segmentsRef.current;
    if (!segments.length) return;
    const run = ++finalizeRunRef.current;
    let blob: Blob;
    try {
      blob = await mergeRecordings(segments);
    } catch {
      // A take failed to decode — keep the raw concatenation so at least the
      // first take still plays/transcribes.
      blob = new Blob(segments, { type: mimeRef.current || undefined });
    }
    if (finalizeRunRef.current !== run) return; // superseded by a newer stop/reset
    finalAudioRef.current = blob;
    revokeAudioUrl();
    audioUrlRef.current = URL.createObjectURL(blob);
    setAudioUrl(audioUrlRef.current);
  }, []);

  const stopAudioCapture = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    if (recorder && recorder.state !== 'inactive') {
      recorderStoppedRef.current = new Promise<void>((resolve) => {
        recorder.onstop = () => {
          void finalizeAudio().finally(resolve);
        };
        try {
          recorder.stop();
        } catch {
          void finalizeAudio().finally(resolve);
        }
      });
    }
    const videoRecorder = videoRecorderRef.current;
    videoRecorderRef.current = null;
    if (videoRecorder && videoRecorder.state !== 'inactive') {
      videoStoppedRef.current = new Promise<void>((resolve) => {
        videoRecorder.onstop = () => {
          if (videoChunksRef.current.length) {
            revokeVideoUrl();
            const blob = new Blob(videoChunksRef.current, { type: videoMimeRef.current || undefined });
            videoUrlRef.current = URL.createObjectURL(blob);
            setVideoUrl(videoUrlRef.current);
          }
          resolve();
        };
        try {
          videoRecorder.stop();
        } catch {
          resolve();
        }
      });
    }
    setPreviewStream(null);
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  }, [finalizeAudio]);

  const startAudioCapture = useCallback(async () => {
    if (!audioSupported) return;
    const audioConstraint = microphoneId ? { deviceId: { exact: microphoneId } } : true;
    let stream: MediaStream | null = null;
    let withVideo = false;
    setVideoError('');
    if (wantVideo) {
      try {
        // Modest resolution keeps recordings small enough to store in SQLite.
        stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraint,
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        });
        withVideo = stream.getVideoTracks().length > 0;
      } catch {
        // No camera / permission denied: fall back to audio-only below.
        setVideoError('Camera unavailable or permission denied — recording audio only.');
      }
    }
    try {
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint });
      }
      // The user may have hit Stop while the permission prompt was open.
      if (!recordingRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      mediaStreamRef.current = stream;
      // The audio recorder always sees a PURE audio stream (even in video
      // mode), so on-device Whisper never has to parse a video container.
      const audioStream = withVideo ? new MediaStream(stream.getAudioTracks()) : stream;
      const mime = pickMimeType();
      const recorder = mime
        ? new MediaRecorder(audioStream, { mimeType: mime })
        : new MediaRecorder(audioStream);
      mimeRef.current = recorder.mimeType || mime;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start(1000); // collect in 1s chunks so a crash loses at most a second
      mediaRecorderRef.current = recorder;

      if (withVideo) {
        try {
          const videoMime = pickVideoMimeType();
          const videoRecorder = videoMime
            ? new MediaRecorder(stream, { mimeType: videoMime })
            : new MediaRecorder(stream);
          videoMimeRef.current = videoRecorder.mimeType || videoMime;
          videoRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) videoChunksRef.current.push(e.data);
          };
          videoRecorder.start(1000);
          videoRecorderRef.current = videoRecorder;
          setPreviewStream(stream);
        } catch {
          setVideoError('This platform cannot encode webcam video — recording audio only.');
        }
      }
    } catch {
      // Mic denied/unavailable: transcription (if supported) and the timer
      // still work — audio playback is simply skipped.
    }
  }, [audioSupported, microphoneId, wantVideo]);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    finalRef.current = transcript ? transcript + ' ' : '';
    setInterim('');
    startTimeRef.current = Date.now() - elapsedSeconds * 1000;
    recordingRef.current = true;
    setRecording(true);
    stopTimer();
    timerRef.current = setInterval(() => {
      setElapsedSeconds(Math.round((Date.now() - startTimeRef.current) / 1000));
    }, 250);

    void startAudioCapture();

    if (!Ctor) return; // No live transcription: timer + audio run, user types transcript.
    const rec = new Ctor();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      let interimText = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalRef.current += r[0].transcript + ' ';
        else interimText += r[0].transcript;
      }
      setTranscript(finalRef.current.trim());
      setInterim(interimText);
    };
    rec.onerror = () => {};
    rec.onend = () => {
      // Auto-restart while the user is still recording (Chrome stops on silence).
      if (recognitionRef.current === rec && recordingRef.current) {
        try {
          rec.start();
        } catch {
          /* ignore */
        }
      }
    };
    recognitionRef.current = rec;
    try {
      rec.start();
    } catch {
      /* already started */
    }
  }, [transcript, elapsedSeconds, startAudioCapture]);

  const stop = useCallback(() => {
    recordingRef.current = false;
    setRecording(false);
    stopTimer();
    stopAudioCapture();
    const rec = recognitionRef.current;
    recognitionRef.current = null;
    if (rec) {
      rec.onend = null;
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    const finalTranscript = (finalRef.current + interim).trim() || transcript;
    setTranscript(finalTranscript);
    setInterim('');
    return { transcript: finalTranscript, durationSeconds: elapsedSeconds };
  }, [interim, transcript, elapsedSeconds, stopAudioCapture]);

  const reset = useCallback(() => {
    recordingRef.current = false;
    finalRef.current = '';
    setTranscript('');
    setInterim('');
    setElapsedSeconds(0);
    setRecording(false);
    stopTimer();
    stopAudioCapture();
    finalizeRunRef.current++; // cancel any in-flight finalize
    chunksRef.current = [];
    segmentsRef.current = [];
    finalAudioRef.current = null;
    videoChunksRef.current = [];
    revokeAudioUrl();
    revokeVideoUrl();
    setVideoError('');
  }, [stopAudioCapture]);

  const getAudio = useCallback(async (): Promise<RecordedAudio | null> => {
    await recorderStoppedRef.current; // wait for the encoder to flush + merge
    const blob = finalAudioRef.current;
    if (!blob) return null;
    return { mime: blob.type || 'audio/webm', bytes: new Uint8Array(await blob.arrayBuffer()) };
  }, []);

  const getVideo = useCallback(async (): Promise<RecordedAudio | null> => {
    await videoStoppedRef.current; // wait for the encoder to flush
    if (!videoChunksRef.current.length) return null;
    const blob = new Blob(videoChunksRef.current, { type: videoMimeRef.current || undefined });
    return { mime: blob.type || 'video/webm', bytes: new Uint8Array(await blob.arrayBuffer()) };
  }, []);

  useEffect(
    () => () => {
      stopTimer();
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      if (videoUrlRef.current) URL.revokeObjectURL(videoUrlRef.current);
    },
    [],
  );

  return {
    supported,
    audioSupported,
    recording,
    transcript,
    interim,
    elapsedSeconds,
    audioUrl,
    videoUrl,
    previewStream,
    videoError,
    start,
    stop,
    reset,
    setTranscript,
    getAudio,
    getVideo,
  };
}

// --- Text to speech --------------------------------------------------------

export interface TextToSpeech {
  supported: boolean;
  speaking: boolean;
  voices: SpeechSynthesisVoice[];
  speak: (text: string, voiceName?: string) => void;
  cancel: () => void;
}

export function useTextToSpeech(): TextToSpeech {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    if (!supported) return;
    const load = () => setVoices(window.speechSynthesis.getVoices());
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [supported]);

  const speak = useCallback(
    (text: string, voiceName?: string) => {
      if (!supported || !text) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const voice = voiceName ? voices.find((v) => v.name === voiceName) : undefined;
      if (voice) u.voice = voice;
      u.rate = 1;
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    },
    [supported, voices],
  );

  const cancel = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  return { supported, speaking, voices, speak, cancel };
}
